import React from 'react';
import { useTranslation } from 'react-i18next';
import './Outreach.css';

const OutreachAnalytics = ({ campaign }) => {
    const { t } = useTranslation('sourcing');

    // Use stats from campaign or fallbacks
    const stats = campaign?.stats || {
        total: 0,
        sent: 0,
        completed: 0,
        active: 0,
        replied: 0
    };

    // Calculate percentage - use totalCandidates (backend) with fallback to total (legacy)
    const total = stats.totalCandidates || stats.total || 0;
    const completed = stats.completed || 0;
    const percentage = stats.completionPercentage !== undefined ? stats.completionPercentage : (total > 0 ? Math.round((completed / total) * 100) : 0);

    // Generate stepsData from campaign nodes
    const allNodes = campaign?.nodes || [];
    const stepsData = allNodes
        .map((n, originalIndex) => ({ ...n, originalIndex }))
        .filter(n => n.type === 'email')
        .map((n, filteredIndex) => {
            const stepId = filteredIndex + 1;
            const stepStats = stats.stepStats?.[n.originalIndex] || { sent: 0, replied: 0 };
            const contacted = stepStats.sent || 0;
            const replied = stepStats.replied || 0;

            return {
                id: n.id || n.originalIndex,
                step: n.title || `Step ${stepId} - Email`,
                contacted: contacted,
                replied: replied,
                completed: total > 0 ? `${Math.round((contacted / total) * 100)}%` : '0%'
            };
        });

    return (
        <div className="outreach-analytics-view">

            {/* Top Dashboard Card */}
            <div className="analytics-summary-card">
                <div className="analytics-ring-container">
                    <div style={{
                        width: '80px', height: '80px', borderRadius: '50%',
                        background: `conic-gradient(#2ecc71 0% ${percentage}%, rgba(255,255,255,0.1) ${percentage}% 100%)`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        position: 'relative'
                    }}>
                        <div style={{
                            width: '68px', height: '68px', borderRadius: '50%',
                            background: 'var(--app-surface, #152238)', // Matches card bg
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            fontSize: '20px', fontWeight: "var(--app-weight-strong, 800)", color: 'var(--app-tone-2ecc71, #2ecc71)'
                        }}>
                            {percentage}%
                        </div>
                    </div>
                    <div className="analytics-ring-label">{t('outreach.stats.complete', 'Complete')}</div>
                </div>

                <div className="analytics-stat-block">
                    <div className="stat-value">{stats.totalCandidates || stats.total || 0}</div>
                    <div className="stat-desc">{t('outreach.stats.totalCandidates', 'Total Candidates')}</div>
                </div>

                <div className="analytics-stat-block">
                    <div className="stat-value">{stats.completed}</div>
                    <div className="stat-desc">{t('outreach.stats.completedSequences', 'Completed Sequences')}</div>
                </div>

                <div className="analytics-stat-block">
                    <div className="stat-value">{stats.active}</div>
                    <div className="stat-desc">{t('outreach.stats.activeSequences', 'Active Sequences')}</div>
                </div>

                <div className="analytics-stat-block">
                    <div className="stat-value replied">{stats.replied}</div>
                    <div className="stat-desc">{t('outreach.stats.replied', 'Replied')}</div>
                </div>
            </div>

            {/* Steps Analytics Table */}
            <h3 className="analytics-section-title">{t('outreach.analytics.byStep', 'Analytics - By Sequence Step')}</h3>

            <div className="candidates-table-container">
                <table className="candidates-table analytics-table">
                    <thead>
                        <tr>
                            <th>{t('outreach.cols.emailStep', 'Email Step')}</th>
                            <th>{t('outreach.cols.contacted', 'Candidates Contacted')}</th>
                            <th>{t('outreach.cols.replied', 'Candidates Replied')}</th>
                            <th>{t('outreach.cols.completed', '% Completed')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {stepsData.map(row => (
                            <tr key={row.id}>
                                <td className="font-bold white-text">{row.step}</td>
                                <td>{row.contacted}</td>
                                <td>{row.replied}</td>
                                <td>{row.completed}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

        </div>
    );
};

export default OutreachAnalytics;
